/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: LoggingSettingsFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for Logging Settings form.
────────────────────────────────────────────────────────────────*/

import { Terminal, FileText } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import type { LoggingSettings } from '../types';
import { LOG_LEVEL_OPTIONS } from '../types';

interface LoggingSettingsFormFieldsProps {
  formData: LoggingSettings;
  updateConsoleSettings: (field: keyof LoggingSettings['consoleSettings'], value: unknown) => void;
  updateFileLogging: (field: keyof LoggingSettings['fileLogging'], value: unknown) => void;
  updateProfile: (index: number, field: string, value: unknown) => void;
  updateProfileMapping: (profileIndex: number, mappingIndex: number, field: string, value: unknown) => void;
  updateEnabled: (value: boolean) => void;
}

export function LoggingSettingsFormFields({
  formData,
  updateConsoleSettings,
  updateFileLogging,
  updateProfile,
  updateProfileMapping,
  updateEnabled
}: LoggingSettingsFormFieldsProps) {
  const { t } = useI18n();

  return (
    <div className="mt-8">
      {/* Console Logging Section */}
      <FormBox title={t('loggingSettings.console.title')} icon={<Terminal className="h-5 w-5" />} className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center mb-6">
          {/* Colorize Output */}
          <Label>
            <SimpleTooltip content={t('loggingSettings.console.tooltips.colorize')}>
              {t('loggingSettings.console.colorize')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle
              value={formData.consoleSettings.colorize}
              onChange={(value) => updateConsoleSettings('colorize', value)}
            />
          </div>

          {/* Enable Logging */}
          <Label>
            {t('loggingSettings.general.enabled')}
          </Label>
          <div>
            <Toggle
              value={formData.enabled}
              onChange={updateEnabled}
            />
          </div>

          {/* Console Log Level */}
          <div className="col-span-4">
            <SimpleTooltip content={t('loggingSettings.console.tooltips.loglevel')}>
              <Label className="mb-2 block">{t('loggingSettings.console.loglevel')}</Label>
            </SimpleTooltip>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 border border-border rounded-lg bg-card">
              {LOG_LEVEL_OPTIONS.map((level) => {
                const isSelected = formData.consoleSettings.loglevel.includes(level);
                return (
                  <label
                    key={level}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-accent/50 p-2 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const currentLevels = formData.consoleSettings.loglevel;
                        if (e.target.checked) {
                          updateConsoleSettings('loglevel', [...currentLevels, level]);
                        } else {
                          updateConsoleSettings('loglevel', currentLevels.filter(l => l !== level));
                        }
                      }}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm">{t(`loggingSettings.levels.${level}`)}</span>
                  </label>
                );
              })}
            </div>
            {formData.consoleSettings.loglevel.length === 0 && (
              <p className="text-sm text-destructive mt-2">
                {t('loggingSettings.console.atLeastOneLevelRequired')}
              </p>
            )}
          </div>
        </div>
      </FormBox>

      {/* File Logging Section */}
      <FormBox title={t('loggingSettings.file.title')} icon={<FileText className="h-5 w-5" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center mb-6">
          {/* Rotate on HUP */}
          <Label>
            <SimpleTooltip content={t('loggingSettings.file.tooltips.rotateOnHup')}>
              {t('loggingSettings.file.rotateOnHup')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle
              value={formData.fileLogging.rotate_on_hup}
              onChange={(value) => updateFileLogging('rotate_on_hup', value)}
            />
          </div>

          {/* UUID Prefix */}
          {formData.fileLogging.profiles.length > 0 && (
            <>
              <Label>
                <SimpleTooltip content={t('loggingSettings.file.tooltips.uuidPrefix')}>
                  {t('loggingSettings.file.uuidPrefix')}
                </SimpleTooltip>
              </Label>
              <div>
                <Toggle
                  value={formData.fileLogging.profiles[0].uuid_prefix}
                  onChange={(value) => updateProfile(0, 'uuid_prefix', value)}
                />
              </div>
            </>
          )}
        </div>

        {/* File Profile Settings - Fixed single profile */}
        {formData.fileLogging.profiles.length > 0 && (() => {
          const profile = formData.fileLogging.profiles[0];
          return (
            <div className="space-y-3">
              {/* Profile Settings */}
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
                  {/* Rollover (Max File Size) */}
                  <Label>
                    <SimpleTooltip content={t('loggingSettings.file.tooltips.rollover')}>
                      {t('loggingSettings.file.rollover')}
                    </SimpleTooltip>
                  </Label>
                  <div>
                    <Input
                      type="number"
                      value={profile.rollover}
                      onChange={(e) => updateProfile(0, 'rollover', parseInt(e.target.value) || 0)}
                      placeholder="1048576000"
                    />
                  </div>
                  {/* Maximum Rotate */}
                  <Label>
                    <SimpleTooltip content={t('loggingSettings.file.tooltips.maximumRotate')}>
                      {t('loggingSettings.file.maximumRotate')}
                    </SimpleTooltip>
                  </Label>
                  <div>
                    <Input
                      type="number"
                      min="1"
                      max="1000"
                      value={profile.maximum_rotate}
                      onChange={(e) => updateProfile(0, 'maximum_rotate', parseInt(e.target.value) || 32)}
                      placeholder="32"
                    />
                  </div>
                </div>
              </div>

              {/* Profile Mappings - Fixed single mapping */}
              {profile.mappings.length > 0 && (() => {
                const mapping = profile.mappings[0];
                return (
                  <div>
                    {/* Log Levels Selection */}
                    <Label className="text-sm mb-2 block">{t('loggingSettings.file.mappingLevels')}</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 border border-border rounded-lg bg-card">
                      {LOG_LEVEL_OPTIONS.map((level) => {
                        const isSelected = mapping.levels.includes(level);
                        return (
                          <label
                            key={level}
                            className="flex items-center space-x-2 cursor-pointer hover:bg-accent/50 p-2 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const currentLevels = mapping.levels;
                                if (e.target.checked) {
                                  updateProfileMapping(0, 0, 'levels', [...currentLevels, level]);
                                } else {
                                  updateProfileMapping(0, 0, 'levels', currentLevels.filter(l => l !== level));
                                }
                              }}
                              className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                            />
                            <span className="text-sm">{t(`loggingSettings.levels.${level}`)}</span>
                          </label>
                        );
                      })}
                    </div>
                    {mapping.levels.length === 0 && (
                      <p className="text-sm text-destructive mt-2">
                        {t('loggingSettings.console.atLeastOneLevelRequired')}
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </FormBox>
    </div>
  );
}

